import React, { useEffect, useMemo, useState, useContext } from 'react'
import styled from 'styled-components'
import ProgressBar from './ProgressBar'
import CommentCount from './../comments/CommentCount'
import StartProgram from './StartProgram'
import moment from 'moment'
import InstructorList from 'components_v1/ClassView/InstructorList'
import { HeartIconComponent } from 'components_v1/ClassView/ClassActions'
import { axios } from 'api'
import { camelToSnake } from 'utils'
import LikesAndDislikes from 'components_v1/LikesAndDislikes'
import ShareNative from 'components_v1/ClassView/ShareNative'
import { useWindowResize } from 'hooks'
import { ProgramContext, ProgramViewContext } from 'components_v2/page_context'

const Container = styled.div`
  width: 100%;
  .meta-info {
    display: flex;
    flex-direction: column;
    gap: 4px;
    div {
      width: fit-content;
    }
  }
  .action-buttons {
    height: fit-content;
    margin-top: 26px;
    @media screen and (min-width: 768px) {
      display: flex;
      gap: ${(state) => `${state.actionGap}px !important`};
    }
  }
`

const ProgramInfo = ({
  id,
  completed,
  total,
  productTitle,
  userPresent = false,
  status,
  completedTime,
  fetchPlan,
  setPlan,
  plan,
  user,
  type,
  authorName
}) => {
  const [saveId, setSaveId] = useState(plan?.planSubscriptions?.[0]?.id || null)
  const { tab } = useContext(ProgramViewContext)
  userPresent ||= Boolean(user)
  id ||= plan?.id
  type ||= 'Plan'
  productTitle ||= plan?.title
  status ||= plan?.programStatus
  completed ||= plan?.completedClassesCount
  completedTime ||= plan?.completedAt
  total ||= plan?.totalClassesCount
  const singleClass = plan?.singleClass || false
  const isPlan = type === 'Plan'

  const [programStatus, setProgramStatus] = useState(status)
  const [completedCount, setCompletedCount] = useState(completed)

  const [loading, setLoading] = useState(false)
  useEffect(() => {
    saveId === null && setSaveId(plan?.planSubscriptions?.[0]?.id)
  }, [plan, saveId])

  useEffect(() => setProgramStatus(status), [status])
  const shouldRenderStartProgram =
    useMemo(
      () =>
        programStatus === null ||
        !['in_progress', 'completed'].includes(programStatus),
      [programStatus]
    ) &&
    isPlan &&
    plan?.unlocked &&
    userPresent &&
    !singleClass

  const handleSave = async () => {
    if (loading) return
    let id = null
    try {
      setLoading(true)
      if (Boolean(saveId)) {
        id = saveId
        setSaveId(0)
        await axios.delete(`/plan_subscriptions/${id}`)
      } else {
        setSaveId(1)
        const res = await axios.post('/plan_subscriptions', {
          plan_id: plan.id
        })
        if (res.data.id) {
          setSaveId(res.data.id)
          window.dataLayer.push(camelToSnake(res?.data?.ga4Events))
        } else {
          setSaveId(0)
          fetchPlan()
        }
      }
    } catch (err) {
      console.log(err)
      id && setSaveId(id)
      fetchPlan()
    } finally {
      setLoading(false)
    }
  }
  const coachList = plan?.coaches?.map((c) => ({
    slug: c?.slug,
    fullName: [c?.firstName, c?.lastName].join(' ')
  }))

  const { width } = useWindowResize()
  const actionGap = Math.min(16, Math.floor(width / 80))

  return (
    <ProgramContext.Provider
      value={{ programStatus, setProgramStatus, setCompletedCount }}
    >
      <Container actionGap={actionGap}>
        {isPlan && userPresent && (
          <ProgressBar
            id={id}
            completed={completedCount}
            total={total}
            status={programStatus}
          />
        )}
        <div className='info-container'>
          <div className='basic-information'>
            <div className='page-type'>
              <h2>{isPlan ? 'Program' : 'Bundle'}</h2>
            </div>
            <div className='video-big-title'>{productTitle}</div>
            <div className='video-meta-info meta-info'>
              <div>
                <span className='author-text'>Instructor: </span>
                <span className='author-name'>
                  {plan?.coaches ? (
                    <InstructorList list={plan?.coaches || []} />
                  ) : (
                    <span dangerouslySetInnerHTML={{ __html: authorName }} />
                  )}
                </span>
              </div>
              {isPlan && (
                <>
                  {completedTime && programStatus === 'completed' && (
                    <div>
                      <span className='author-text'>Last completed: </span>
                      <span className='author-name'>
                        <span
                          dangerouslySetInnerHTML={{
                            __html: moment(new Date(completedTime)).format(
                              'MM/DD/YYYY'
                            )
                          }}
                        />
                      </span>
                    </div>
                  )}
                  <div>{tab === 'overview' && <CommentCount />}</div>
                </>
              )}
            </div>
            {shouldRenderStartProgram && (
              <StartProgram
                id={id}
                status={programStatus}
                setStatus={setProgramStatus}
              />
            )}
          </div>
          {isPlan && (
            <div className='action-buttons pdp-series-actions'>
              {plan?.unlocked && (
                <>
                  <div className='pdp-series-save' data-pdp-action='save'>
                    <HeartIconComponent
                      isFilled={Boolean(saveId)}
                      onClick={handleSave}
                      id='series-favourite-button'
                    />
                  </div>
                  <LikesAndDislikes
                    handleChange={(data) =>
                      setPlan((state) => ({
                        ...state,
                        likes: data?.likes,
                        userLiked: data?.userLiked
                      }))
                    }
                    classDetails={{
                      id: plan.id,
                      title: plan.title,
                      style: plan.style
                    }}
                    username={user?.username}
                    createdAt={user?.createdAt}
                    type={'SERIES'}
                    coaches={coachList}
                  />
                </>
              )}

              <div className='pdp-series-share'>
                <ShareNative
                  plan_entry={plan}
                  shareKind='series'
                  triggerButtonClass={'pdp-series-share-native-btn'}
                  wrapperClass='pdp-series-share-native-wrap'
                />
              </div>
            </div>
          )}
        </div>
      </Container>
    </ProgramContext.Provider>
  )
}

export default ProgramInfo
